import * as R from 'ramda'
import { createSelector } from 'reselect'
import { SEARCH_ENGINE_OPE_CODE } from 'constants/referer'
import { isWebviewInterfaceAvailable } from 'domain/header'
import { getDeviceInfo } from 'helpers/useragent'
import {
  GetApplication,
  GetCountryCode,
  GetCultureCode,
  GetDevice,
  GetDeviceScreenSize,
  GetDeviceType,
  GetFeatures,
  GetFeaturesViewProductsMap,
  GetHasMomentLocaleBeenSet,
  GetIsBackToHomeFeatureEnabled,
  GetIsHideReferencePriceFeatureEnabled,
  GetIsHotjarFeatureEnabled,
  GetIsLastViewedProductFeatureEnabled,
  GetIsNavigationRedirectionButtonFeatureEnabled,
  GetIsOneTrustFeatureEnabled,
  GetIsPackageUnitDisclaimerFeatureEnabled,
  GetIsPaymentBannerFeatureEnabled,
  GetIsPreviewMode,
  GetIsProdEnv,
  GetIsProductsMapOpen,
  GetIsQuoteTOSFeatureEnabled,
  GetIsReviewsFeatureEnabled,
  GetIsSaleChanging,
  GetIsSaleFetched,
  GetIsToggleCheapestPriceFeatureEnabled,
  GetIsViewAllOffersFeatureEnabled,
  GetLocaleFromApplication,
  GetMarketId,
  GetMediaPath,
  GetOperationCode,
  GetOperationId,
  GetPartner,
  GetRefererURL,
  GetRefererURLOpeCode,
  GetSaleKey,
  GetScreenSize,
  GetShouldDoubleThePriceFeatureEnabled,
  GetTemplate,
  GetThemeName,
  GetTypeOfAccess
} from './application.types'

const getApplication: GetApplication = R.prop('application')

export const getOperationCode: GetOperationCode = createSelector(
  getApplication,
  R.prop('operationCode')
)

export const getMarketId: GetMarketId = createSelector(
  getApplication,
  R.prop('marketId')
)

const getFeatures: GetFeatures = createSelector(
  getApplication,
  R.prop('features')
)

export const getFeaturesViewProductsMap: GetFeaturesViewProductsMap = createSelector(
  getFeatures,
  R.prop('viewProductsMap')
)

export const getSaleKey: GetSaleKey = createSelector(
  getApplication,
  R.prop('saleKey')
)

export const getIsSaleFetched: GetIsSaleFetched = createSelector(
  getApplication,
  R.prop('isSaleFetched')
)

export const getIsSaleChanging: GetIsSaleChanging = createSelector(
  getApplication,
  R.prop('isSaleChanging')
)

export const getIsPreviewMode: GetIsPreviewMode = createSelector(
  getApplication,
  R.prop('isPreview')
)

export const getIsProdEnv: GetIsProdEnv = createSelector( // Deprecated ?
  getApplication,
  ({ env }) => env === 'PROD'
)

export const getIsProductsMapOpen: GetIsProductsMapOpen = createSelector(
  getApplication,
  R.prop('isProductsMapOpen')
)

export const getDevice: GetDevice = createSelector(
  getApplication,
  R.prop('device')
)

export const getMediaPath: GetMediaPath = createSelector(
  getApplication,
  R.path(['paths', 'mediaPath'])
)

export const getIsBackToHomeFeatureEnabled: GetIsBackToHomeFeatureEnabled = createSelector(
  getFeatures,
  R.prop('backToHome')
)

export const getIsReviewsFeatureEnabled: GetIsReviewsFeatureEnabled = createSelector(
  getFeatures,
  R.prop('reviews')
)

export const getIsHotjarFeatureEnabled: GetIsHotjarFeatureEnabled = createSelector(
  getFeatures,
  R.prop('hotjar')
)

export const getIsOneTrustFeatureEnabled: GetIsOneTrustFeatureEnabled = createSelector(
  getFeatures,
  R.prop('oneTrust')
)

export const getIsPackageUnitDisclaimerFeatureEnabled: GetIsPackageUnitDisclaimerFeatureEnabled = createSelector(
  getFeatures,
  features => Boolean(features?.shouldDisclaimPackagePriceUnits)
)

export const getIsLastViewedProductFeatureEnabled: GetIsLastViewedProductFeatureEnabled = createSelector(
  getFeatures,
  feature => {
    const isLastViewedProductFeatureEnabled = R.prop('lastViewedProduct', feature)

    return getDeviceInfo().webview
      ? isLastViewedProductFeatureEnabled && isWebviewInterfaceAvailable('updateHeader')
      : isLastViewedProductFeatureEnabled
  }
)

export const getIsHideReferencePriceFeatureEnabled: GetIsHideReferencePriceFeatureEnabled = createSelector(
  getFeatures,
  R.prop('shouldHideReferencePrice')
)

export const getIsPaymentBannerFeatureEnabled: GetIsPaymentBannerFeatureEnabled = createSelector(
  getFeatures,
  R.prop('paymentBanner')
)

export const getIsQuoteTOSFeatureEnabled: GetIsQuoteTOSFeatureEnabled = createSelector(
  getFeatures,
  R.prop('displayQuoteTOS')
)

export const getIsNavigationRedirectionButtonFeatureEnabled: GetIsNavigationRedirectionButtonFeatureEnabled = createSelector(
  getFeatures,
  R.prop('shouldShowNavigationRedirectionButton')
)

export const getIsToggleCheapestPriceFeatureEnabled: GetIsToggleCheapestPriceFeatureEnabled = createSelector(
  getFeatures,
  R.prop('toggleCheapestPrice')
)

export const getIsViewAllOffersFeatureEnabled: GetIsViewAllOffersFeatureEnabled = createSelector(
  getFeatures,
  R.prop('viewAllOffers')
)

export const getOperationId: GetOperationId = createSelector(
  getApplication,
  R.prop('operationId')
)

export const getScreenSize: GetScreenSize = createSelector(
  getApplication,
  R.prop('screenSize')
)

export const getDeviceScreenSize: GetDeviceScreenSize = createSelector(
  getScreenSize,
  (screenSize) => ({
    desktop: screenSize.large,
    desktopHD: screenSize.large,
    mobile: screenSize.small,
    tablet: screenSize.medium
  })
)

export const getDeviceType: GetDeviceType = createSelector(
  getDevice,
  getScreenSize,
  (device, screenSize) => {
    // handle small screens on web
    if (device?.type === 'web' && !screenSize?.large) {
      return 'mobile'
    } else {
      return device?.type
    }
  }
)

export const getThemeName: GetThemeName = createSelector(
  getApplication,
  R.prop('theme')
)

export const getPartner: GetPartner = createSelector(
  getApplication,
  R.prop('partner')
)

export const getCultureCode: GetCultureCode = createSelector(
  getApplication,
  R.path(['locale', 'cultureCode'])
)

export const getCountryCode: GetCountryCode = createSelector(
  getApplication,
  R.path(['locale', 'countryCode'])
)

export const getLocaleFromApplication: GetLocaleFromApplication = createSelector(
  getCultureCode,
  getCountryCode,
  (cultureCode, countryCode) => `${cultureCode}-${countryCode}`
)

export const getHasMomentLocaleBeenSet: GetHasMomentLocaleBeenSet = createSelector(
  getApplication,
  R.path(['locale', 'hasMomentLocaleBeenSet'])
)

export const getTemplate: GetTemplate = createSelector(
  getApplication,
  R.prop('template')
)

export const getRefererURL: GetRefererURL = createSelector(
  getApplication,
  R.prop('referer')
)

export const getRefererUrlOpeCode: GetRefererURLOpeCode = createSelector(
  getRefererURL,
  refererURL => refererURL
    ?.match(/#\/([a-zA-Z\d_]+)\//)
    ?.[1]
    ?.toUpperCase() ?? undefined
)

export const getTypeOfAccess: GetTypeOfAccess = createSelector(
  getRefererUrlOpeCode,
  refererOpeCode => !refererOpeCode
    ? 'TSF'
    : refererOpeCode === SEARCH_ENGINE_OPE_CODE
      ? 'Travel search engine'
      : 'Landing page'
)

export const getShouldDoubleThePriceFeatureEnabled: GetShouldDoubleThePriceFeatureEnabled = createSelector(
  getFeatures,
  features => Boolean(features?.shouldDoubleThePrice)
)
